import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Container,
  Tabs,
  Tab,
} from "@mui/material";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDeleteDialog from "../../../../shared/ConfirmDeleteDialog";
import ConfirmStopModal from "../../../../shared/ConfirmStopModal";
import UserModal from "../../../users/UserModal";
import PaginatedListLayout from "../../../../shared/pagination/PaginatedListLayout";
import MemberTable from "../../shared/MemberTable";

export default function MemberListPage({ currentCompanyUser }) {
  const navigate = useNavigate();
  const abortRef = useRef(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const [flashMessage, setFlashMessage] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalMode, setModalMode] = useState("new");
  const [editUserData, setEditUserData] = useState(null);

  const [companyRoleKey, setCompanyRoleKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [tab, setTab] = useState("all");

  // 登録メール確認モーダル用
  const [showRegisterMailModal, setShowRegisterMailModal] = useState(false);
  const [registerMailTargetUser, setRegisterMailTargetUser] = useState(null);
  const [isSendingRegisterMail, setIsSendingRegisterMail] = useState(false);

  // 共通：ユーザー作成/更新API処理
  const submitUserForm = async ({ fd, method, url, successMessage }) => {
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;

    return fetch(url, {
      method,
      headers: { "X-CSRF-Token": csrfToken },
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 422) {
            const data = await res.json().catch(() => ({}));

            if (Array.isArray(data?.errors)) {
              return Promise.reject(data.errors);
            }

            if (data?.errors && typeof data.errors === "object") {
              const messages = Object.entries(data.errors).flatMap(
                ([field, msgs]) =>
                  Array.isArray(msgs)
                    ? msgs.map((m) => `${field} ${m}`)
                    : [`${field} ${msgs}`],
              );
              return Promise.reject(messages);
            }

            return Promise.reject(["バリデーションエラーが発生しました。"]);
          }

          const text = await res.text().catch(() => "");
          throw new Error(`Request failed: ${res.status}\n${text}`);
        }

        return res.json();
      })
      .then(() => {
        toast.success(successMessage);
        setShowUserModal(false);
        fetchMembers(currentPage, tab);
      })
      .catch((err) => {
        if (Array.isArray(err)) throw err;
        console.error(err);
        toast.error("処理に失敗しました");
        throw err;
      });
  };

  // 新規追加 POST
  const handleSubmitNew = async (fd) => {
    if (fd instanceof FormData) {
      fd.set("company_user[role]", "employee");
    }

    return submitUserForm({
      fd,
      method: "POST",
      url: "/company/users.json",
      successMessage: "メンバーを追加しました",
    });
  };

  // 編集 PUT
  const handleSubmitEdit = async (fd) => {
    const userId = editUserData?.id;
    if (!userId) {
      toast.error("編集対象のユーザーが取得できません");
      return;
    }

    return submitUserForm({
      fd,
      method: "PUT",
      url: `/company/users/${userId}.json`,
      successMessage: "メンバーを更新しました",
    });
  };

  // 初期ロード・ユーザー情報取得
  useEffect(() => {
    const el = document.querySelector("[data-company-role]");
    setCompanyRoleKey(el?.dataset?.companyRole ?? null);
  }, []);

  // 一覧取得を共通関数化
  const fetchMembers = useCallback((page = 1, currentTab = "all") => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    const params = new URLSearchParams({
      page: String(page),
      status: currentTab,
    });

    fetch(`/company/members.json?${params.toString()}`, {
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        setMembers(data.users || []);
        setPagination(data.pagination || null);

        // サーバー側でページ番号が丸められた場合に同期する
        const serverPage = data.pagination?.current_page ?? page;
        if (serverPage !== page) {
          setCurrentPage(serverPage);
        }

        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setLoading(false);
      });
  }, []);

  // フラッシュメッセージ
  useEffect(() => {
    // SPA遷移時はlocation.stateで拾う
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      // 履歴クリアも忘れずに
      window.history.replaceState({}, document.title, location.pathname);
      return;
    }

    // SSR/リロード時はdata属性から拾う
    const root = document.querySelector(".react-app-router");
    if (root && root.dataset.flashMessage) {
      try {
        setFlashMessage(JSON.parse(root.dataset.flashMessage));
        // data属性消しておくと安全（同じメッセージが何度も表示されるのを防ぐため）
        root.dataset.flashMessage = "";
      } catch (e) {
        setFlashMessage(root.dataset.flashMessage);
        root.dataset.flashMessage = "";
      }
    }
  }, [location.key, location.state, location.pathname]);

  // フラッシュメッセージがセットされたら上部にスクロール
  useEffect(() => {
    if (flashMessage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [flashMessage]);

  // 削除ダイアログを開く
  const handleOpenDialog = (user) => {
    setTargetUser(user);
    setShowDialog(true);
  };

  // 削除ダイアログを閉じる（削除中は不可）
  const handleCloseDialog = () => {
    if (isDeleting) return;
    setShowDialog(false);
    setTargetUser(null);
  };

  /**
   * 「削除」を確定したときの処理
   * DELETE リクエストを送り、完了後にページをリロード
   */
  const handleConfirmDelete = () => {
    if (isDeleting) return;
    setIsDeleting(true);
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;

    fetch(`/company/members/${targetUser?.id}.json`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    }).then((res) => {
      setIsDeleting(false);

      if (res.ok) {
        setShowDialog(false);
        setTargetUser(null);
        toast.success("メンバーを削除しました");
        fetchMembers(currentPage, tab);
      } else {
        toast.error("削除に失敗しました");
      }
    });
  };

  /**
   * 登録メール確認モーダルを開く
   */
  const handleOpenRegisterMailModal = (user) => {
    setRegisterMailTargetUser(user);
    setShowRegisterMailModal(true);
  };

  /**
   * 登録メール確認モーダルを閉じる
   */
  const handleCloseRegisterMailModal = () => {
    if (isSendingRegisterMail) return;
    setShowRegisterMailModal(false);
    setRegisterMailTargetUser(null);
  };

  /**
   * 登録メール送信確定
   */
  const handleConfirmSendRegisterMail = async () => {
    if (!registerMailTargetUser || isSendingRegisterMail) return;

    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;

    setIsSendingRegisterMail(true);

    try {
      const res = await fetch(
        `/company/members/${registerMailTargetUser.id}/send_register_mail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
        },
      );

      if (!res.ok) {
        throw new Error("登録メールの送信に失敗しました");
      }

      toast.success("登録メールを送信しました");
      setShowRegisterMailModal(false);
      setRegisterMailTargetUser(null);
      fetchMembers(currentPage, tab);
    } catch (error) {
      console.error(error);
      toast.error("登録メールの送信に失敗しました");
    } finally {
      setIsSendingRegisterMail(false);
    }
  };

  // タブ切り替え
  const handleTabChange = (_event, newValue) => {
    setTab(newValue);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  // ページ切り替え
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  // 初回マウント時・画面復帰時・条件変更時にメンバー一覧を取得
  useEffect(() => {
    fetchMembers(currentPage, tab);
  }, [location.key, currentPage, tab, fetchMembers]);

  // ローディング
  if (loading && members.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 10 }}>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  // JSX
  return (
    <>
      {/* フラッシュメッセージ*/}
      {flashMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert
            severity={
              typeof flashMessage === "object" ? flashMessage.type : "success"
            }
          >
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      <Container sx={{ mt: 4, mb: 10 }}>
        <Box
          sx={{
            position: "sticky",
            top: "63px",
            zIndex: 20,
            backgroundColor: "#F4F7FA",
            boxShadow: 0,
            mx: -2,
            px: 2,
            pt: 1.5,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="h5" fontWeight="bold">
              メンバー管理
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setModalMode("new");
                setShowUserModal(true);
              }}
              sx={{ fontWeight: "bold" }}
            >
              ＋ 追加
            </Button>
          </Box>

          <Box
            sx={{
              display: "inline-block",
              borderBottom: "1px solid #E5E7EB",
              mb: 2,
            }}
          >
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              textColor="inherit"
              TabIndicatorProps={{
                style: {
                  height: 3,
                  borderRadius: 3,
                  backgroundColor: "#1976d2",
                },
              }}
              sx={{
                minHeight: 48,
                "& .MuiTabs-flexContainer": {
                  gap: 3,
                },
              }}
            >
              {[
                { label: "全員", value: "all" },
                { label: "本登録", value: "registered" },
                { label: "仮登録", value: "temporary" },
                ...(currentCompanyUser?.role === "admin"
                  ? [{ label: "削除済み", value: "deleted" }]
                  : []),
              ].map((item) => (
                <Tab
                  key={item.value}
                  label={item.label}
                  value={item.value}
                  disableRipple
                  sx={{
                    minHeight: 48,
                    px: 0.5,
                    minWidth: "auto",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "none",
                    "&.Mui-selected": {
                      color: "#111827",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
        </Box>

        <PaginatedListLayout
          items={members}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyText="メンバーが登録されていません"
        >
          <MemberTable
            members={members}
            currentPage={currentPage}
            pagination={pagination}
            currentCompanyUser={currentCompanyUser}
            navigate={navigate}
            onEdit={(user) => {
              setModalMode("edit");
              setEditUserData(user);
              setShowUserModal(true);
            }}
            onDelete={handleOpenDialog}
            onSendRegisterMail={handleOpenRegisterMailModal}
          />
        </PaginatedListLayout>
      </Container>

      {/* 削除確認ダイアログ */}
      <ConfirmDeleteDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
      />

      {/* 登録メール確認モーダル */}
      <ConfirmStopModal
        open={showRegisterMailModal}
        onClose={handleCloseRegisterMailModal}
        onConfirm={handleConfirmSendRegisterMail}
        title="登録メールを送信"
        message={`${
          registerMailTargetUser?.full_name ?? "このメンバー"
        }（${registerMailTargetUser?.email ?? ""}）に登録メールを送信します。`}
        subMessage="送信してもよろしいですか？"
        confirmLabel="送信する"
      />

      {/* 新規・追加モーダル */}
      <UserModal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
        mode={modalMode}
        initialValues={
          modalMode === "edit" ? editUserData : { role: "employee" }
        }
        onSubmit={modalMode === "edit" ? handleSubmitEdit : handleSubmitNew}
        currentCompanyUser={currentCompanyUser}
        allUsers={members}
        companyRoleKey={companyRoleKey}
      />
    </>
  );
}
